import { useEffect, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { useTour } from '@reactour/tour';
import { usePostHog } from 'posthog-js/react';
import routesConfig from 'constants/routesConfig.json';

// context & hooks
import { useUpdateTourCompletedMutation } from 'shared/hooks/api/user';
import useCanAccessConversations from 'eCoach/hooks/useCanAccessConversations';

// types
import type { User } from 'shared/hooks/api/user';

interface UseTourStateOptions {
  user?: User;
}

interface UseTourStateReturn {
  isOpen: boolean;
  startTour: () => void;
}

const useTourState = ({ user }: UseTourStateOptions): UseTourStateReturn => {
  const history = useHistory();
  const posthog = usePostHog();
  const { isOpen, setIsOpen, setCurrentStep } = useTour();
  const { mutate: completeTour } = useUpdateTourCompletedMutation();
  const canAccessShiftNotes = posthog.isFeatureEnabled('shift_notes');
  const canAccessConversations = useCanAccessConversations();

  const startTour = useCallback(() => {
    const route = user?.is_demo
      ? `${routesConfig.ALL_CONVOS_PAGE}?tour=true`
      : `${routesConfig.CONVO_PAGE}?tour=true`;
    history.replace(route);

    completeTour();

    setTimeout(() => {
      if (!isOpen) {
        setIsOpen(true);
        setCurrentStep(0);
      }
    }, 500);
  }, [history, setCurrentStep, setIsOpen, isOpen, completeTour, user?.is_demo]);

  useEffect(() => {
    const hasCompletedTour = user?.member?.tour_completed_at !== null;

    if (!hasCompletedTour && !canAccessShiftNotes && canAccessConversations)
      startTour();
  }, [startTour, user, canAccessShiftNotes, canAccessConversations]);

  return { isOpen, startTour };
};

export default useTourState;
